import type { Size } from './types';
import { Development, General } from '../StrokeIcons';
import WaveSpinner from '../WaveSpinner/WaveSpinner';
import Button from './Button';
import MiddleEllipsis from './MiddleEllipsis';

/**
 * A single live sequence enrollment for a contact. `status` is the coarse enrollment
 * lifecycle (active / waiting / blocked / human / errored) — carried for future tinting;
 * the badge renders the same regardless today.
 */
export type ActiveSequenceEnrollment = {
  /** flow.sequence.public_id — the id passed back to `onUnenroll`. */
  sequencePublicId: string;
  name: string;
  status?: string;
};

export type SequenceEnrollmentBadgeProps = {
  sequence: ActiveSequenceEnrollment;
  size?: Size;
  /** Remove the contact from this sequence. When omitted, the trash affordance is hidden (read-only). */
  onUnenroll?: (sequencePublicId: string) => void;
  isUnenrolling?: boolean;
};

/**
 * The "in sequence: X" pill shown on the contact detail card, mirroring the active-cadence pill:
 * a coloured, read-only name pill plus a trash button that unenrolls. Its presence is what BLOCKS
 * the enroll dropdown (see {@link CampaignButtonWithCadence}) — a contact already on a journey can't
 * be double-enrolled; you remove them here first. Uses the app-wide sequence icon (Dataflow04).
 */
export default function SequenceEnrollmentBadge({
  sequence,
  size = 'sm',
  onUnenroll,
  isUnenrolling,
}: SequenceEnrollmentBadgeProps) {
  return (
    <div className="flex items-center gap-1 min-w-0 max-w-full">
      <Button
        size={size}
        color="aurora"
        readOnly
        icon={<Development.Dataflow04 />}
        tooltip={`In sequence: ${sequence.name}`}
        className="min-w-0 font-semibold tracking-[-0.02em]">
        <MiddleEllipsis text={sequence.name} />
      </Button>
      {onUnenroll && (
        <Button
          size={size}
          color="red"
          text
          icon={isUnenrolling ? <WaveSpinner className="size-full" /> : <General.Trash01 />}
          disabled={isUnenrolling}
          onClick={() => onUnenroll(sequence.sequencePublicId)}
          shape="pill"
          bordered={false}
          tooltip="Remove from sequence"
        />
      )}
    </div>
  );
}
